import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useSearch } from "@redotech/react-util/search";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { CarrierAndServiceDropdown } from "@redotech/redo-merchant-fulfillment-app/common/carrier-and-service-dropdown";
import { getLabelText } from "@redotech/redo-merchant-fulfillment-app/common/shipment-rate-selection-dropdown";
import { OutboundRateOption } from "@redotech/redo-merchant-fulfillment-app/common/util";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { omsActionStepSchema } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  AutoHoldType,
  GeneratePersonalizedSlipActionStep,
  OmsActionType,
  OmsSettableFields,
  SetDeliveryMethodActionStep,
  SetPackingSlipTemplateActionStep,
  SetParcelActionStep,
  SetShippingServiceActionStep,
  SetShippingServiceCheapestActionStep,
  SetShippingServiceCheapestWithinDeliveryDaysActionStep,
  SetShippingServiceFastestActionStep,
  SetThirdPartyBillingAccountActionStep,
  WeightRoundingType,
} from "@redotech/redo-model/advanced-flow/oms/actions";
import {
  OmsActionStep,
  StepType,
} from "@redotech/redo-model/advanced-flow/steps";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import {
  CarrierAndService,
  carrierToLabel,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import {
  RedoShopifyDeliveryMethodType,
  RedoShopifyDeliveryMethodTypeToString,
} from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import { SortingCriteria } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { ShopifyFulfillmentHoldReason } from "@redotech/redo-model/fulfillments/shopify-fulfillment-schemas";
import { Parcel } from "@redotech/redo-model/outbound-labels/parcel";
import {
  convertWeight,
  WeightUnit,
} from "@redotech/redo-model/outbound-labels/util";
import { PackingSlip } from "@redotech/redo-model/packing-slips/packing-slip-definition";
import { PersonalizedSlip } from "@redotech/redo-model/personalized-slip/personalized-slip-definition";
import { TimeUnit } from "@redotech/redo-model/time";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  LogoSize,
  ShippingLogo,
} from "@redotech/redo-web/fulfillment/common/shipping-logo";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { FrontendStepType, PartialWithRequiredType } from "..";
import { TagInput } from "../add-tags";
import { HoldDateInput, HoldDaysInput, HoldDetailsInput } from "../place-hold";
import { RoundWeightInput } from "../round-weight";
import { UserDropdownInput } from "../set-assigned-user";
import { BuyReturnLabelToggle } from "../set-buy-return-label";
import { DutiesPayerRadioGroup } from "../set-duties-payer";
import { FulfillmentServiceDropdown } from "../set-fulfillment-service";
import { OriginLocationDropdown } from "../set-origin-location";
import { OverrideUseOriginalDestinationAddressToggle } from "../set-override-use-original-destination-address";
import { ReturnAddressDropdown } from "../set-return-address";
import { TaxIdentifiersDropdown } from "../set-tax-identifiers";
import { WeightInput } from "../set-weight";
import { ShippingOptionsInput } from "../shipping-options";
import { useCarrierAccountsForAutomation } from "./use-carrier-accounts";

const OmsShippingServiceInputs = memo(function OmsShippingServiceInputs({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetShippingServiceActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetShippingServiceActionStep>,
  ) => void;
}) {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    !!step.carrierAccountId,
  );

  const setShippingService = useHandler((option: CarrierAndService) => {
    setStep({
      ...step,
      newShippingService: option.service,
      newShippingCarrier: option.carrier,
    });
  });

  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { carrierAccounts, carrierAccountsLoading } =
    useCarrierAccountsForAutomation();

  const carrierAccountOptions: RedoListItem<string>[] = useMemo(
    () =>
      carrierAccounts
        .filter((account) => !!account.carrierAccountId)
        .map((account) => ({
          id: account.carrierAccountId!,
          value: account.carrierAccountId!,
          type: "text",
          text: account.name || "Merchant Account",
        })),
    [carrierAccounts],
  );

  const options = useLoad(async (signal: AbortSignal) => {
    const { options } = await client.getCarrierServiceCombinations(
      { includeDisabled: false, includeMerchantCarriers: true },
      { signal },
    );
    return options;
  }, []);

  const setCarrierAccount = useHandler(
    ({ item }: { item: RedoListItem<string> }) => {
      setStep({ ...step, carrierAccountId: item.value });
    },
  );

  const hasCarrierAccounts = carrierAccounts.length > 0;

  const { checkboxDisabled, checkboxTooltip } = useMemo(() => {
    if (carrierAccountsLoading) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: "Loading carrier accounts...",
      };
    }
    if (!hasCarrierAccounts) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: "No merchant carrier accounts available",
      };
    }
    return { checkboxDisabled: false, checkboxTooltip: undefined };
  }, [carrierAccountsLoading, hasCarrierAccounts]);

  return (
    <Flex dir="column" gap="md">
      <CarrierAndServiceDropdown
        itemsLoading={options.pending}
        onSelectionChange={setShippingService}
        options={options.value ?? []}
        selectedOption={
          step.newShippingCarrier && step.newShippingService
            ? {
                carrier: step.newShippingCarrier,
                service: step.newShippingService,
              }
            : null
        }
      />
      <Flex dir="column" gap="xs" py="xs">
        <RedoCheckbox
          disabled={checkboxDisabled}
          label="Advanced carrier mapping"
          setValue={(value) => {
            setShowAdvancedOptions(value === true);
            if (value !== true) {
              setStep({ ...step, carrierAccountId: null });
            }
          }}
          size="sm"
          value={showAdvancedOptions}
        />
        {checkboxTooltip && (
          <Text fontSize="xs" textColor="secondary">
            {checkboxTooltip}
          </Text>
        )}
        {showAdvancedOptions && hasCarrierAccounts && (
          <Flex dir="column" gap="xs">
            <RedoSingleSelectDropdownInput<string>
              fitToAnchor
              flexGrow
              options={carrierAccountOptions}
              optionSelected={setCarrierAccount}
              placeholder="Select account"
              selectedItem={carrierAccountOptions.find(
                (opt) => opt.value === step.carrierAccountId,
              )}
              size="xs"
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const OmsShippingServiceGroupInputs = memo(function OmsShippingServiceInputs({
  step,
  setStep,
  selectionCriteria,
}: {
  step:
    | PartialWithRequiredType<SetShippingServiceCheapestActionStep>
    | PartialWithRequiredType<SetShippingServiceFastestActionStep>
    | PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>;
  setStep: (
    step:
      | PartialWithRequiredType<SetShippingServiceCheapestActionStep>
      | PartialWithRequiredType<SetShippingServiceFastestActionStep>
      | PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>,
  ) => void;
  selectionCriteria: SortingCriteria;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { carrierAccounts, carrierAccountsLoading } =
    useCarrierAccountsForAutomation();

  const hasAnyCarrierAccount = useMemo(() => {
    return (
      step.servicesToChooseFrom?.some((s) => s.carrierAccountId) ||
      step.servicesToPrioritize?.some((s) => s.carrierAccountId)
    );
  }, [step.servicesToChooseFrom, step.servicesToPrioritize]);

  const [showAdvancedOptions, setShowAdvancedOptions] =
    useState(hasAnyCarrierAccount);

  const hasCarrierAccounts = carrierAccounts.length > 0;

  const { checkboxDisabled, checkboxTooltip } = useMemo(() => {
    if (carrierAccountsLoading) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: "Loading carrier accounts...",
      };
    }
    if (!hasCarrierAccounts) {
      return {
        checkboxDisabled: true,
        checkboxTooltip: "No merchant carrier accounts available",
      };
    }
    return { checkboxDisabled: false, checkboxTooltip: undefined };
  }, [carrierAccountsLoading, hasCarrierAccounts]);

  const carrierAndServiceOptionLoad = useLoad(async (signal: AbortSignal) => {
    const { options } = await client.getCarrierServiceCombinations(
      { includeDisabled: false, includeMerchantCarriers: true },
      { signal },
    );
    return options;
  }, []);

  // I swear I didn't want to do this but lint made me
  // The 'carrierAndServiceOptions' logical expression could make the dependencies of useMemo Hook (at line 391) change on every render. To fix this, wrap the initialization of 'carrierAndServiceOptions' in its own useMemo() Hook.
  const carrierAndServiceOptions = useMemo(() => {
    return carrierAndServiceOptionLoad.value ?? [];
  }, [carrierAndServiceOptionLoad.value]);

  // Handler for base services selection
  const setShippingServices = (options: OutboundRateOption[]) => {
    setStep({
      ...step,
      selectionCriteria,
      servicesToChooseFrom: options.map((option) => {
        // Preserve existing carrierAccountId if the service already exists
        const existing = step.servicesToChooseFrom?.find(
          (s) => s.carrier === option.carrier && s.service === option.service,
        );
        return {
          carrier: option.carrier,
          service: option.service,
          carrierAccountId: existing?.carrierAccountId ?? null,
        };
      }),
    } as PartialWithRequiredType<
      SetShippingServiceCheapestActionStep | SetShippingServiceFastestActionStep
    >);
  };

  // Handler for priority services selection
  const setPriorityServices = (options: OutboundRateOption[]) => {
    setStep({
      ...step,
      selectionCriteria,
      servicesToPrioritize: options.map((option) => {
        // Preserve existing carrierAccountId if the service already exists
        const existing = step.servicesToPrioritize?.find(
          (s) => s.carrier === option.carrier && s.service === option.service,
        );
        return {
          carrier: option.carrier,
          service: option.service,
          carrierAccountId: existing?.carrierAccountId ?? null,
        };
      }),
    } as PartialWithRequiredType<
      SetShippingServiceCheapestActionStep | SetShippingServiceFastestActionStep
    >);
  };

  const removeShippingService = useHandler(
    (option: BadgeRedoListItem<OutboundRateOption>) => {
      const oldServices = step.servicesToChooseFrom || [];
      const newServices = oldServices.filter(
        (service) =>
          !!service &&
          !!service.carrier &&
          !!service.service &&
          (service?.carrier !== option.value.carrier ||
            service?.service !== option.value.service),
      );
      setShippingServices(newServices as OutboundRateOption[]);
    },
  );

  const removePriorityService = useHandler(
    (option: BadgeRedoListItem<OutboundRateOption>) => {
      const oldServices = step.servicesToPrioritize || [];
      const newServices = oldServices.filter(
        (service) =>
          !!service &&
          !!service.carrier &&
          !!service.service &&
          (service?.carrier !== option.value.carrier ||
            service?.service !== option.value.service),
      );
      setPriorityServices(newServices as OutboundRateOption[]);
    },
  );

  const onDropdownValueChange = useHandler(
    (values: RedoListItem<OutboundRateOption>[]) => {
      setShippingServices(values.map((value) => value.value));
    },
  );

  const onPriorityDropdownValueChange = useHandler(
    (values: RedoListItem<OutboundRateOption>[]) => {
      setPriorityServices(values.map((value) => value.value));
    },
  );

  // State for base services dropdown
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [searchString, setSearchString] = useState("");

  const handleSetDropdownOpen = useHandler((open: boolean) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchString("");
    }
  });

  // State for priority services dropdown
  const [priorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [priorityDropdownButtonRef, setPriorityDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const baseSelectedOptions: CarrierAndService[] = useMemo(() => {
    if (!step.servicesToChooseFrom) {
      return [];
    }

    return filterTruthy(
      step.servicesToChooseFrom.map((option) => {
        if (!option || !option.carrier || !option.service) {
          return undefined;
        }
        return option as CarrierAndService;
      }),
    );
  }, [step.servicesToChooseFrom]);

  const options: RedoListItem<CarrierAndService>[] = useMemo(() => {
    return carrierAndServiceOptions.map((option) => {
      return {
        id: `${option.carrier}-${option.service}`,
        value: option,
        type: "text",
        text: getLabelText(option),
      };
    });
  }, [carrierAndServiceOptions]);

  const searcher = useMemo(
    () => new Fuse(options, { keys: ["text"], threshold: 0.3 }),
    [options],
  );

  const filteredOptions = useMemo(() => {
    if (!searchString.trim()) {
      return options;
    }
    return searcher.search(searchString).map((result) => result.item);
  }, [options, searchString, searcher]);

  const priorityOptions: RedoListItem<CarrierAndService>[] = useMemo(() => {
    // Only show services that have been selected in the base services dropdown
    return baseSelectedOptions.map((option) => {
      return {
        id: `${option.carrier}-${option.service}`,
        value: option,
        type: "text",
        text: getLabelText(option),
      };
    });
  }, [baseSelectedOptions]);

  const prioritySelectedOptions: CarrierAndService[] = useMemo(() => {
    if (!step.servicesToPrioritize) {
      return [];
    }

    return filterTruthy(
      step.servicesToPrioritize.map((option) => {
        if (!option || !option.carrier || !option.service) {
          return undefined;
        }
        return option as CarrierAndService;
      }),
    );
  }, [step.servicesToPrioritize]);

  const selectedButtonOptions: BadgeRedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return baseSelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "badge",
          badge: {
            text: getLabelText({
              carrier: option.carrier,
              service: option.service,
            }),
            segmentLeading: {
              Icon: () => (
                <ShippingLogo carrier={option.carrier} size={LogoSize.MEDIUM} />
              ),
              type: "icon",
            },
          },
        };
      });
    }, [baseSelectedOptions]);

  const priorityButtonOptions: BadgeRedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return prioritySelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "badge",
          badge: {
            text: getLabelText({
              carrier: option.carrier,
              service: option.service,
            }),
            segmentLeading: {
              Icon: () => (
                <ShippingLogo carrier={option.carrier} size={LogoSize.MEDIUM} />
              ),
              type: "icon",
            },
          },
        };
      });
    }, [prioritySelectedOptions]);

  const selectedDropdownOptions: RedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return baseSelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "text",
          text: getLabelText(option),
        };
      });
    }, [baseSelectedOptions]);

  const priorityDropdownOptions: RedoListItem<CarrierAndService>[] =
    useMemo(() => {
      return prioritySelectedOptions.map((option) => {
        return {
          id: `${option.carrier}-${option.service}`,
          value: option,
          type: "text",
          text: getLabelText(option),
        };
      });
    }, [prioritySelectedOptions]);

  const onSelectAll = useHandler(() => {
    if (step.servicesToChooseFrom?.length !== carrierAndServiceOptions.length) {
      setShippingServices(carrierAndServiceOptions);
    } else {
      setShippingServices([]);
    }
  });

  const selectAllState = useMemo(() => {
    if (step.servicesToChooseFrom?.length === carrierAndServiceOptions.length) {
      return true;
    }
    return false;
  }, [step.servicesToChooseFrom, carrierAndServiceOptions]);

  const setCarrierAccountForService = useHandler(
    (carrier: string, service: string, carrierAccountId: string) => {
      const updatedServicesToChooseFrom = step.servicesToChooseFrom?.map(
        (s) => {
          if (s.carrier === carrier && s.service === service) {
            return { ...s, carrierAccountId };
          }
          return s;
        },
      );

      const updatedServicesToPrioritize = step.servicesToPrioritize?.map(
        (s) => {
          if (s.carrier === carrier && s.service === service) {
            return { ...s, carrierAccountId };
          }
          return s;
        },
      );

      setStep({
        ...step,
        servicesToChooseFrom: updatedServicesToChooseFrom,
        servicesToPrioritize: updatedServicesToPrioritize,
      } as PartialWithRequiredType<
        | SetShippingServiceCheapestActionStep
        | SetShippingServiceFastestActionStep
        | SetShippingServiceCheapestWithinDeliveryDaysActionStep
      >);
    },
  );

  const carrierAccountOptions: RedoListItem<string>[] = useMemo(
    () =>
      carrierAccounts
        .filter((account) => !!account.carrierAccountId)
        .map((account) => ({
          id: account.carrierAccountId!,
          value: account.carrierAccountId!,
          type: "text" as const,
          text: account.name || "Merchant Account",
        })),
    [carrierAccounts],
  );

  return (
    <Flex dir="column" gap="md">
      <div
        style={{ maxHeight: "20rem", overflow: "auto", position: "relative" }}
      >
        <RedoTagsDropdownInputButton
          isDropdownOpen={dropdownOpen}
          onClick={() => handleSetDropdownOpen(!dropdownOpen)}
          placeholder="Select services"
          ref={setDropdownButtonRef}
          removeItem={removeShippingService}
          selectedItems={selectedButtonOptions}
          size="xs"
        />
      </div>

      {selectionCriteria === SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS && (
        <>
          <Text fontSize="xs" fontWeight="medium">
            Deliver within
          </Text>
          <RedoIncrementDecrement
            min={1}
            setValue={(value) => {
              setStep({
                ...step,
                selectionCriteria,
                targetDeliveryDays: value,
              } as PartialWithRequiredType<SetShippingServiceCheapestWithinDeliveryDaysActionStep>);
            }}
            size="small"
            step={1}
            suffix="days"
            value={
              "targetDeliveryDays" in step ? (step.targetDeliveryDays ?? 1) : 1
            }
          />
        </>
      )}
      <RedoMultiselectDropdown<CarrierAndService>
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        dropdownPlacement="left-start"
        fitToAnchor
        items={filteredOptions}
        searchPlaceholder="Search carriers and services..."
        searchString={searchString}
        selectAllState={selectAllState}
        selectAllToggled={onSelectAll}
        selectedItems={selectedDropdownOptions}
        selectionVariant="checkbox"
        setDropdownOpen={handleSetDropdownOpen}
        setSearchString={setSearchString}
        setSelectedItems={onDropdownValueChange}
        size="xs"
      />
      {selectionCriteria !== SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS && (
        <>
          <Text fontSize="xs" fontWeight="medium">
            Prioritize services (optional)
          </Text>
          <Flex align="flex-start">
            <RedoTagsDropdownInputButton
              isDropdownOpen={priorityDropdownOpen}
              onClick={() => setPriorityDropdownOpen(!priorityDropdownOpen)}
              placeholder="Select priority services"
              ref={setPriorityDropdownButtonRef}
              removeItem={removePriorityService}
              selectedItems={priorityButtonOptions}
              size="xs"
            />
            <RedoMultiselectDropdown<CarrierAndService>
              dropdownAnchor={priorityDropdownButtonRef}
              dropdownOpen={priorityDropdownOpen}
              dropdownPlacement="left-start"
              fitToAnchor
              items={priorityOptions}
              selectedItems={priorityDropdownOptions}
              selectionVariant="checkbox"
              setDropdownOpen={setPriorityDropdownOpen}
              setSelectedItems={onPriorityDropdownValueChange}
              size="xs"
            />
          </Flex>
        </>
      )}
      {selectionCriteria !== SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS && (
        <>
          <Text fontSize="xs" fontWeight="medium">
            if within
          </Text>
          <RedoIncrementDecrement
            min={0}
            prefix="$"
            setValue={(value) => {
              setStep({
                ...step,
                selectionCriteria,
                maxPriorityPriceDifference: value,
              } as PartialWithRequiredType<
                | SetShippingServiceCheapestActionStep
                | SetShippingServiceFastestActionStep
              >);
            }}
            size="small"
            step={0.01}
            value={step.maxPriorityPriceDifference ?? 0}
          />
        </>
      )}
      <Flex dir="column" gap="xs" py="xs">
        <RedoCheckbox
          disabled={checkboxDisabled}
          label="Advanced carrier mapping"
          setValue={(value) => {
            setShowAdvancedOptions(value === true);
            if (value !== true) {
              // Clear all carrier account IDs when disabling advanced options
              const clearedServicesToChooseFrom =
                step.servicesToChooseFrom?.map((s) => ({
                  ...s,
                  carrierAccountId: null,
                }));
              const clearedServicesToPrioritize =
                step.servicesToPrioritize?.map((s) => ({
                  ...s,
                  carrierAccountId: null,
                }));
              setStep({
                ...step,
                servicesToChooseFrom: clearedServicesToChooseFrom,
                servicesToPrioritize: clearedServicesToPrioritize,
              } as PartialWithRequiredType<
                | SetShippingServiceCheapestActionStep
                | SetShippingServiceFastestActionStep
                | SetShippingServiceCheapestWithinDeliveryDaysActionStep
              >);
            }
          }}
          size="sm"
          value={showAdvancedOptions ?? false}
        />
        {checkboxTooltip && (
          <Text fontSize="xs" textColor="secondary">
            {checkboxTooltip}
          </Text>
        )}
        {showAdvancedOptions &&
          hasCarrierAccounts &&
          step.servicesToChooseFrom && (
            <Flex dir="column" gap="sm">
              <Text fontSize="xs" fontWeight="medium">
                Carrier Accounts
              </Text>
              {step.servicesToChooseFrom
                .filter((service) => service.carrier && service.service)
                .map((service) => {
                  if (!service.carrier || !service.service) {
                    return null;
                  }
                  return (
                    <Flex
                      align="center"
                      dir="row"
                      gap="md"
                      key={`${service.carrier}-${service.service}`}
                    >
                      <Flex flex={1}>
                        <Text fontSize="xs">
                          {getLabelText({
                            carrier: service.carrier,
                            service: service.service,
                          })}
                        </Text>
                      </Flex>
                      <Flex flex={1}>
                        <RedoSingleSelectDropdownInput<string>
                          fitToAnchor={false}
                          flexGrow
                          options={carrierAccountOptions}
                          optionSelected={({ item }) => {
                            setCarrierAccountForService(
                              service.carrier!,
                              service.service!,
                              item.value,
                            );
                          }}
                          placeholder="Select account"
                          selectedItem={carrierAccountOptions.find(
                            (opt) => opt.value === service.carrierAccountId,
                          )}
                          size="xs"
                        />
                      </Flex>
                    </Flex>
                  );
                })}
            </Flex>
          )}
      </Flex>
    </Flex>
  );
});

const ParcelInput = memo(function ParcelInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetParcelActionStep>;
  setStep: (step: PartialWithRequiredType<SetParcelActionStep>) => void;
}) {
  const outboundLabelRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );
  const parcelsLoad = useLoad(async () => {
    const result = await outboundLabelRpcClient.getParcels(null);
    return result.parcels;
  }, []);

  const parcelOptions: RedoListItem<Parcel>[] = useMemo(() => {
    if (!parcelsLoad.value) {
      return [];
    }

    return parcelsLoad.value.map((parcel) => ({
      value: parcel,
      id: parcel.name,
      type: "text",
      text: parcel.name,
    }));
  }, [parcelsLoad.value]);

  const virtualize = parcelOptions.length > 100;

  const { hasExistingOverrides, currentParcelNames } = useMemo(() => {
    // Mongoose returns an empty array if no parcels are set which is bad
    const currentParcelNames = step.parcels?.length
      ? step.parcels
      : step.parcel
        ? [step.parcel]
        : [];
    const hasExistingOverrides = currentParcelNames.some(
      (p) => !!p?.parcelAndContentWeight,
    );
    return { hasExistingOverrides, currentParcelNames };
  }, [step.parcels, step.parcel]);

  const [showWeightOverrides, setShowWeightOverrides] =
    useState(hasExistingOverrides);

  const updateParcel = useHandler((index: number, parcel: Parcel) => {
    const newParcels = [...currentParcelNames];
    newParcels[index] = {
      ...parcel,
      parcelAndContentWeight: newParcels[index]?.parcelAndContentWeight,
    };
    setStep({ ...step, parcels: newParcels, parcel: newParcels[0] });
  });

  const removeParcel = useHandler((index: number) => {
    const newParcels = currentParcelNames.filter((_, i) => i !== index);
    setStep({ ...step, parcels: newParcels, parcel: newParcels[0] });
  });

  const addParcel = useHandler(() => {
    const firstParcel = parcelsLoad.value?.[0];
    if (firstParcel) {
      const newParcels = [...currentParcelNames, firstParcel];
      setStep({ ...step, parcels: newParcels, parcel: newParcels[0] });
    }
  });

  const toggleWeightOverrides = useHandler((enabled: boolean) => {
    setShowWeightOverrides(enabled);
    if (!enabled) {
      const newParcels = currentParcelNames.map((p) => ({
        ...p,
        parcelAndContentWeight: undefined,
      }));
      setStep({ ...step, parcels: newParcels });
    }
  });

  const updateOverrideWeightPounds = useHandler(
    (index: number, pounds: number) => {
      if (!showWeightOverrides) return;
      const currentWeight = currentParcelNames[index]
        ?.parcelAndContentWeight ?? { value: 0, unit: WeightUnit.OUNCE };
      const currentOunces =
        currentWeight?.unit === WeightUnit.OUNCE
          ? (currentWeight.value ?? 0) % 16
          : 0;
      const totalOunces = pounds * 16 + currentOunces;
      const newParcels = [...currentParcelNames];
      newParcels[index] = {
        ...newParcels[index],
        parcelAndContentWeight: { value: totalOunces, unit: WeightUnit.OUNCE },
      };
      setStep({ ...step, parcels: newParcels });
    },
  );

  const updateOverrideWeightOunces = useHandler(
    (index: number, ounces: number) => {
      if (!showWeightOverrides) return;
      const currentWeight = currentParcelNames[index]?.parcelAndContentWeight;
      const currentPounds =
        currentWeight?.unit === WeightUnit.OUNCE
          ? Math.floor((currentWeight.value ?? 0) / 16)
          : 0;
      const totalOunces = currentPounds * 16 + ounces;
      const newParcels = [...currentParcelNames];
      newParcels[index] = {
        ...newParcels[index],
        parcelAndContentWeight: { value: totalOunces, unit: WeightUnit.OUNCE },
      };
      setStep({ ...step, parcels: newParcels });
    },
  );

  const hasMultipleParcels = currentParcelNames.length > 1;

  return (
    <Flex dir="column" gap="sm" w="full">
      {hasMultipleParcels && (
        <Flex align="center" gap="xs">
          <input
            checked={showWeightOverrides}
            onChange={(e) => toggleWeightOverrides(e.target.checked)}
            type="checkbox"
          />
          <Text fontSize="xs" fontWeight="medium">
            Override weights
          </Text>
        </Flex>
      )}
      {currentParcelNames.map((stepParcel, index) => {
        const parcel = parcelsLoad.value?.find(
          (p) => p.name === stepParcel?.name,
        );
        const overrideWeight = stepParcel?.parcelAndContentWeight ?? {
          value: 0,
          unit: WeightUnit.OUNCE,
        };

        let pounds = 0;
        let ounces = 0;

        const convertedWeight = convertWeight(
          {
            value: overrideWeight.value ?? 0,
            unit: overrideWeight.unit ?? WeightUnit.OUNCE,
          },
          WeightUnit.OUNCE,
        );
        pounds = Math.floor(convertedWeight.value / 16);
        ounces = convertedWeight.value % 16;

        return (
          <Flex dir="column" gap="xs" key={index}>
            <Flex align="center" gap="sm">
              <RedoSingleSelectDropdownInput<Parcel>
                flexGrow
                options={parcelOptions}
                optionSelected={({ item }) => updateParcel(index, item.value)}
                placeholder="Select parcel"
                selectedItem={
                  parcel
                    ? {
                        value: parcel,
                        id: parcel.name,
                        type: "text",
                        text: parcel.name,
                      }
                    : undefined
                }
                size="xs"
                virtualize={virtualize}
              />
              {currentParcelNames.length > 1 && (
                <div
                  className={inspectorStyles.closeButton}
                  onClick={() => removeParcel(index)}
                >
                  <XIcon height="16" width="16" />
                </div>
              )}
            </Flex>
            {hasMultipleParcels && showWeightOverrides && (
              <Flex dir="row" gap="md">
                <Flex flex={1}>
                  <RedoIncrementDecrement
                    fullWidth
                    min={0}
                    placeholder="0"
                    selectOnFocus
                    setValue={(value) =>
                      updateOverrideWeightPounds(index, value)
                    }
                    size="small"
                    step="any"
                    suffix="lbs"
                    value={pounds}
                  />
                </Flex>
                <Flex flex={1}>
                  <RedoIncrementDecrement
                    fullWidth
                    min={0}
                    placeholder="0"
                    selectOnFocus
                    setValue={(value) =>
                      updateOverrideWeightOunces(index, value)
                    }
                    size="small"
                    step="any"
                    suffix="oz"
                    value={ounces}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        );
      })}
      <Text
        cursor="pointer"
        fontSize="xs"
        fontWeight="medium"
        onClick={addParcel}
      >
        + Add parcel
      </Text>
      {currentParcelNames.length > 0 && (
        <Flex mt="lg">
          <RedoCheckbox
            label="Add to existing parcels"
            setValue={(value) =>
              setStep({ ...step, addToExistingParcels: value === true })
            }
            size="sm"
            value={!!step.addToExistingParcels}
          />
        </Flex>
      )}
    </Flex>
  );
});

const PackingSlipTemplateInput = memo(function PackingSlipTemplateInput({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetPackingSlipTemplateActionStep>;
  setStep: (
    step: PartialWithRequiredType<SetPackingSlipTemplateActionStep>,
  ) => void;
}) {
  const team = useRequiredContext(TeamContext);
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const packingSlipTemplates = useLoad(async (signal) => {
    try {
      const result = await merchantRpcClient.getPackingSlipTemplatesByTeam(
        { teamId: team._id },
        { signal },
      );
      return result || [];
    } catch (error) {
      console.error("Failed to load packing slip templates:", error);
      return [];
    }
  }, []);

  const templateOptions: RedoListItem<PackingSlip>[] = useMemo(() => {
    if (
      !packingSlipTemplates.value ||
      packingSlipTemplates.value.length === 0
    ) {
      return [];
    }

    return packingSlipTemplates.value.map((template) => ({
      id: template._id,
      value: template,
      type: "text" as const,
      text: template.name,
    }));
  }, [packingSlipTemplates.value]);

  const setPackingSlipTemplate = useHandler(
    ({ item }: { item: RedoListItem<PackingSlip> }) => {
      setStep({
        ...step,
        packingSlipTemplateId: item.value._id,
        packingSlipName: item.value.name,
      });
    },
  );

  return (
    <RedoSingleSelectDropdownInput
      fitToAnchor={false}
      flexGrow={false}
      options={templateOptions}
      optionSelected={setPackingSlipTemplate}
      placeholder="Select template"
      selectedItem={templateOptions.find(
        (t) => t.value._id === step.packingSlipTemplateId,
      )}
      size="xs"
    />
  );
});

const PersonalizedInsertTemplateInput = memo(
  function PersonalizedInsertTemplateInput({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>;
    setStep: (
      step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>,
    ) => void;
  }) {
    const team = useRequiredContext(TeamContext);
    const outboundLabelsRpcClient = useRequiredContext(
      RedoOutboundLabelsRpcClientContext,
    );

    const personalizedSlipTemplates = useLoad(async (signal) => {
      try {
        const result =
          await outboundLabelsRpcClient.getPersonalizedSlipTemplatesByTeam(
            { teamId: team._id },
            { signal },
          );
        return result || [];
      } catch (error) {
        console.error("Failed to load personalized slip templates:", error);
        return [];
      }
    }, []);

    const templateOptions: RedoListItem<PersonalizedSlip>[] = useMemo(() => {
      if (
        !personalizedSlipTemplates.value ||
        personalizedSlipTemplates.value.length === 0
      ) {
        return [];
      }

      return personalizedSlipTemplates.value.map((template) => ({
        id: template._id,
        value: template,
        type: "text" as const,
        text: template.name,
      }));
    }, [personalizedSlipTemplates.value]);

    const setPersonalizedSlipTemplate = useHandler(
      ({ item }: { item: RedoListItem<PersonalizedSlip> }) => {
        setStep({
          ...step,
          personalizedSlipTemplateId: item.value._id,
          personalizedSlipTemplateName: item.value.name,
        });
      },
    );

    return (
      <RedoSingleSelectDropdownInput
        fitToAnchor={false}
        flexGrow={false}
        options={templateOptions}
        optionSelected={setPersonalizedSlipTemplate}
        placeholder="Select template"
        selectedItem={templateOptions.find(
          (t) => t.value._id === step.personalizedSlipTemplateId,
        )}
        size="xs"
      />
    );
  },
);

const ThirdPartyBillingAccountDropdown = memo(
  function ThirdPartyBillingAccountDropdown({
    step,
    setStep,
  }: {
    step: PartialWithRequiredType<SetThirdPartyBillingAccountActionStep>;
    setStep: (
      step: PartialWithRequiredType<SetThirdPartyBillingAccountActionStep>,
    ) => void;
  }) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    const billingAccounts = useLoad(
      async (signal) => {
        const result =
          await merchantRpcClient.getThirdPartyBillingAccounts(null);
        return result.accounts || [];
      },
      [merchantRpcClient],
    );

    const billingAccountOptions: RedoListItem<{
      _id: string;
      accountName: string;
      accountNumber: string;
      carrier: string;
    }>[] = useMemo(() => {
      if (!billingAccounts.value) {
        return [];
      }

      return billingAccounts.value.map((account) => ({
        id: account._id.toString(),
        value: account,
        type: "text" as const,
        text: `${account.accountName} (${carrierToLabel[account.carrier]})`,
      }));
    }, [billingAccounts.value]);

    const setBillingAccount = useHandler(
      ({
        item,
      }: {
        item: RedoListItem<{
          _id: string;
          accountName: string;
          accountNumber: string;
          carrier: string;
        }>;
      }) => {
        setStep({
          ...step,
          thirdPartyBillingAccountId: item.value._id,
          thirdPartyBillingAccountName: item.value.accountName,
        });
      },
    );

    return (
      <RedoSingleSelectDropdownInput
        fitToAnchor={false}
        flexGrow={false}
        options={billingAccountOptions}
        optionSelected={setBillingAccount}
        placeholder="Select billing account"
        selectedItem={billingAccountOptions.find(
          (a) => a.value._id === step.thirdPartyBillingAccountId,
        )}
        size="xs"
      />
    );
  },
);

const DeliveryMethodDropdown = memo(function DeliveryMethodDropdown({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<SetDeliveryMethodActionStep>;
  setStep: (step: PartialWithRequiredType<SetDeliveryMethodActionStep>) => void;
}) {
  const deliveryMethodOptions: RedoListItem<RedoShopifyDeliveryMethodType>[] =
    useMemo(() => {
      const availableDeliveryMethods = [
        RedoShopifyDeliveryMethodType.Shipping,
        RedoShopifyDeliveryMethodType.Local,
        RedoShopifyDeliveryMethodType.PickUp,
        RedoShopifyDeliveryMethodType.Freight,
      ];
      return availableDeliveryMethods.map((deliveryMethod) => ({
        id: deliveryMethod,
        value: deliveryMethod,
        type: "text" as const,
        text: RedoShopifyDeliveryMethodTypeToString[deliveryMethod],
      }));
    }, []);

  const setDeliveryMethod = useHandler(
    ({ item }: { item: RedoListItem<RedoShopifyDeliveryMethodType> }) => {
      setStep({ ...step, deliveryMethod: item.value });
    },
  );

  return (
    <RedoSingleSelectDropdownInput
      fitToAnchor={false}
      flexGrow={false}
      options={deliveryMethodOptions}
      optionSelected={setDeliveryMethod}
      placeholder="Select delivery method"
      selectedItem={deliveryMethodOptions.find(
        (a) => a.value === step.deliveryMethod,
      )}
      size="xs"
    />
  );
});

const omsOptions: RedoListItem<OmsSettableFields>[] = Object.values(
  OmsSettableFields,
).map((field) => ({
  id: field,
  type: "text",
  value: field,
  text: omsSettableToFriendly(field),
}));

function omsSettableToFriendly(field: OmsSettableFields) {
  switch (field) {
    case OmsSettableFields.SHIPPING_SERVICE:
      return "Set shipping service";
    case OmsSettableFields.PARCEL:
      return "Set parcel";
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST:
      return "Set shipping service to cheapest option";
    case OmsSettableFields.SHIPPING_SERVICE_FASTEST:
      return "Set shipping service to fastest option";
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS:
      return "Set shipping service to cheapest within delivery days";
    case OmsSettableFields.PACKING_SLIP_TEMPLATE:
      return "Set packing slip template";
    case OmsSettableFields.TAG:
      return "Add tags";
    case OmsSettableFields.SHIPPING_OPTIONS:
      return "Set shipping options";
    case OmsSettableFields.PLACE_HOLD:
      return "Place hold";
    case OmsSettableFields.PLACE_HOLD_UNTIL:
      return "Place hold until";
    case OmsSettableFields.SET_WEIGHT:
      return "Set weight";
    case OmsSettableFields.ROUND_WEIGHT:
      return "Round weight";
    case OmsSettableFields.ASSIGNED_USER:
      return "Set assigned user";
    case OmsSettableFields.DUTIES_PAYER:
      return "Set duties payer";
    case OmsSettableFields.TAX_IDENTIFIERS:
      return "Set tax identifiers";
    case OmsSettableFields.RETURN_ADDRESS:
      return "Set return address";
    case OmsSettableFields.FULFILLMENT_SERVICE:
      return "Assign fulfillment service";
    case OmsSettableFields.ORIGIN_LOCATION:
      return "Set origin location";
    case OmsSettableFields.BUY_RETURN_LABEL:
      return "Set return label preference";
    case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT:
      return "Set third party billing account";
    case OmsSettableFields.DELIVERY_METHOD:
      return "Set delivery method";
    case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS:
      return "Address verification override";
    case OmsSettableFields.PERSONALIZED_SLIP:
      return "Generate personalized insert";
    case OmsSettableFields.INTERNAL_NOTE:
      return "Set internal note";
    case OmsSettableFields.SPLIT_INTO_PARCELS:
      return "Split into parcels";
    default:
      assertNever(field);
  }
}

// search-tag:ADDING-AN-OMS-ACTION
const OmsActionInputs = memo(function OmsActionInputs({
  schema,
  step,
  setStep,
  onClose,
}: {
  step: PartialWithRequiredType<OmsActionStep>;
  setStep: (step: PartialWithRequiredType<OmsActionStep>) => void;
  schema: Schema;
  onClose?: () => void;
}) {
  const [searchString, setSearchString] = useState("");

  const actionSearcher = useMemo(() => {
    return new Fuse<RedoListItem<OmsSettableFields>>([], {
      keys: ["text"],
      threshold: 0.3,
    });
  }, []);

  const filteredOptions = useSearch(actionSearcher, omsOptions, searchString);

  const setActionType = (
    actionType: OmsActionType,
    field: OmsSettableFields,
  ) => {
    setStep({
      actionType,
      field,
      type: StepType.OMS_ACTION,
      nextId: step.nextId,
      id: step.id,
    } as PartialWithRequiredType<OmsActionStep>);
  };
  let joinText = "";
  const setField = useHandler(
    ({ item: fieldListItem }: { item: RedoListItem<OmsSettableFields> }) => {
      switch (fieldListItem.value) {
        case OmsSettableFields.SHIPPING_SERVICE:
          setActionType(
            OmsActionType.SET_SHIPPING_SERVICE,
            OmsSettableFields.SHIPPING_SERVICE,
          );
          break;
        case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST:
          setActionType(
            OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST,
            OmsSettableFields.SHIPPING_SERVICE_CHEAPEST,
          );
          break;
        case OmsSettableFields.SHIPPING_SERVICE_FASTEST:
          setActionType(
            OmsActionType.SET_SHIPPING_SERVICE_FASTEST,
            OmsSettableFields.SHIPPING_SERVICE_FASTEST,
          );
          break;
        case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS:
          setActionType(
            OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
            OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
          );
          break;
        case OmsSettableFields.PARCEL:
          setActionType(OmsActionType.SET_PARCEL, fieldListItem.value);
          break;
        case OmsSettableFields.PACKING_SLIP_TEMPLATE:
          setActionType(
            OmsActionType.SET_PACKING_SLIP_TEMPLATE,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.TAG:
          setActionType(OmsActionType.ADD_TAG, fieldListItem.value);
          break;
        case OmsSettableFields.SHIPPING_OPTIONS:
          setActionType(
            OmsActionType.SET_SHIPPING_OPTIONS,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.PLACE_HOLD:
          setStep({
            actionType: OmsActionType.PLACE_HOLD,
            field: OmsSettableFields.PLACE_HOLD,
            type: StepType.OMS_ACTION,
            nextId: step.nextId,
            id: step.id,
            holdOptions: {
              holdReason: ShopifyFulfillmentHoldReason.Other,
              ...("holdOptions" in step ? step.holdOptions : null),
              holdType: AutoHoldType.Duration,
              duration: 1,
              timeUnit: TimeUnit.DAY,
            },
          } as PartialWithRequiredType<OmsActionStep>);

          break;
        case OmsSettableFields.PLACE_HOLD_UNTIL: {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(9, 0, 0, 0);

          setStep({
            actionType: OmsActionType.PLACE_HOLD_UNTIL,
            field: OmsSettableFields.PLACE_HOLD_UNTIL,
            type: StepType.OMS_ACTION,
            nextId: step.nextId,
            id: step.id,
            holdOptions: {
              holdReason: ShopifyFulfillmentHoldReason.Other,
              ...("holdOptions" in step ? step.holdOptions : null),
              holdType: AutoHoldType.Date,
              date: tomorrow,
            },
          } as PartialWithRequiredType<OmsActionStep>);
          break;
        }
        case OmsSettableFields.SET_WEIGHT:
          setStep({
            actionType: OmsActionType.SET_WEIGHT,
            field: OmsSettableFields.SET_WEIGHT,
            type: StepType.OMS_ACTION,
            nextId: step.nextId,
            id: step.id,
            weight: {
              unit: WeightUnit.OUNCE,
              value: 16, // Default value of 1 pound (16 oz)
            },
          } as PartialWithRequiredType<OmsActionStep>);
          break;
        case OmsSettableFields.ROUND_WEIGHT:
          setStep({
            actionType: OmsActionType.ROUND_WEIGHT,
            field: OmsSettableFields.ROUND_WEIGHT,
            type: StepType.OMS_ACTION,
            nextId: step.nextId,
            id: step.id,
            roundingType: WeightRoundingType.ROUND_UP,
            minimumWeight: {
              unit: WeightUnit.POUND,
              value: 1, // Default minimum of 1 pound
            },
          } as PartialWithRequiredType<OmsActionStep>);
          break;
        case OmsSettableFields.ASSIGNED_USER:
          setActionType(OmsActionType.SET_ASSIGNED_USER, fieldListItem.value);
          break;
        case OmsSettableFields.DUTIES_PAYER:
          setActionType(OmsActionType.SET_DUTIES_PAYER, fieldListItem.value);
          break;
        case OmsSettableFields.TAX_IDENTIFIERS:
          setActionType(OmsActionType.SET_TAX_IDENTIFIERS, fieldListItem.value);
          break;
        case OmsSettableFields.RETURN_ADDRESS:
          setActionType(OmsActionType.SET_RETURN_ADDRESS, fieldListItem.value);
          break;
        case OmsSettableFields.FULFILLMENT_SERVICE:
          setActionType(
            OmsActionType.ASSIGN_FULFILLMENT_SERVICE,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.ORIGIN_LOCATION:
          setActionType(OmsActionType.SET_ORIGIN_LOCATION, fieldListItem.value);
          break;
        case OmsSettableFields.BUY_RETURN_LABEL:
          setActionType(
            OmsActionType.SET_BUY_RETURN_LABEL,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT:
          setActionType(
            OmsActionType.SET_THIRD_PARTY_BILLING_ACCOUNT,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.DELIVERY_METHOD:
          setActionType(OmsActionType.SET_DELIVERY_METHOD, fieldListItem.value);
          break;
        case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS:
          setActionType(
            OmsActionType.SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.PERSONALIZED_SLIP:
          setActionType(
            OmsActionType.GENERATE_PERSONALIZED_SLIP,
            fieldListItem.value,
          );
          break;
        case OmsSettableFields.INTERNAL_NOTE:
          setActionType(OmsActionType.SET_INTERNAL_NOTE, fieldListItem.value);
          break;
        case OmsSettableFields.SPLIT_INTO_PARCELS:
          setActionType(OmsActionType.SPLIT_INTO_PARCELS, fieldListItem.value);
          break;
        default:
          assertNever(fieldListItem.value);
      }
    },
  );

  let row1Fields: React.ReactNode = null;
  let row2Fields: React.ReactNode = null;
  switch (step.field) {
    case OmsSettableFields.SHIPPING_SERVICE: {
      joinText = "to";
      row2Fields = <OmsShippingServiceInputs setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST: {
      joinText = "of";
      row2Fields = (
        <OmsShippingServiceGroupInputs
          selectionCriteria={SortingCriteria.CHEAPEST}
          setStep={setStep}
          step={step}
        />
      );

      break;
    }
    case OmsSettableFields.SHIPPING_SERVICE_FASTEST: {
      joinText = "of";
      row2Fields = (
        <OmsShippingServiceGroupInputs
          selectionCriteria={SortingCriteria.FASTEST}
          setStep={setStep}
          step={step}
        />
      );

      break;
    }
    case OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS: {
      joinText = "of";
      row2Fields = (
        <OmsShippingServiceGroupInputs
          selectionCriteria={SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS}
          setStep={setStep}
          step={step}
        />
      );

      break;
    }
    case OmsSettableFields.PARCEL: {
      row2Fields = <ParcelInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.PACKING_SLIP_TEMPLATE: {
      joinText = "to";
      row1Fields = <PackingSlipTemplateInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.TAG: {
      row2Fields = <TagInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.SHIPPING_OPTIONS: {
      row2Fields = <ShippingOptionsInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.PLACE_HOLD: {
      joinText = "for";
      row1Fields = <HoldDaysInput setStep={setStep} step={step} />;
      row2Fields = <HoldDetailsInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.PLACE_HOLD_UNTIL: {
      row1Fields = <HoldDateInput setStep={setStep} step={step} />;
      row2Fields = <HoldDetailsInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.SET_WEIGHT: {
      joinText = "to";
      row2Fields = <WeightInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.ROUND_WEIGHT: {
      joinText = "";
      row1Fields = <RoundWeightInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.ASSIGNED_USER: {
      joinText = "to";
      row1Fields = <UserDropdownInput setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.DUTIES_PAYER: {
      joinText = "to";
      row2Fields = <DutiesPayerRadioGroup setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.TAX_IDENTIFIERS: {
      joinText = "to";
      row2Fields = <TaxIdentifiersDropdown setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.RETURN_ADDRESS: {
      joinText = "to";
      row2Fields = <ReturnAddressDropdown setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.FULFILLMENT_SERVICE: {
      joinText = "to";
      row2Fields = <FulfillmentServiceDropdown setStep={setStep} step={step} />;

      break;
    }
    case OmsSettableFields.ORIGIN_LOCATION: {
      joinText = "to";
      row2Fields = <OriginLocationDropdown setStep={setStep} step={step} />;
      break;
    }
    case OmsSettableFields.BUY_RETURN_LABEL: {
      joinText = "to";
      row2Fields = <BuyReturnLabelToggle setStep={setStep} step={step} />;
      break;
    }
    case OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT: {
      joinText = "to";
      row2Fields = (
        <ThirdPartyBillingAccountDropdown setStep={setStep} step={step} />
      );
      break;
    }
    case OmsSettableFields.DELIVERY_METHOD: {
      joinText = "to";
      row2Fields = (
        <DeliveryMethodDropdown
          setStep={
            setStep as (
              step: PartialWithRequiredType<SetDeliveryMethodActionStep>,
            ) => void
          }
          step={step as PartialWithRequiredType<SetDeliveryMethodActionStep>}
        />
      );
      break;
    }
    case OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS: {
      row2Fields = (
        <OverrideUseOriginalDestinationAddressToggle
          setStep={setStep}
          step={step}
        />
      );
      break;
    }
    case OmsSettableFields.PERSONALIZED_SLIP: {
      joinText = "using";
      row1Fields = (
        <PersonalizedInsertTemplateInput
          setStep={
            setStep as (
              step: PartialWithRequiredType<GeneratePersonalizedSlipActionStep>,
            ) => void
          }
          step={
            step as PartialWithRequiredType<GeneratePersonalizedSlipActionStep>
          }
        />
      );
      break;
    }
    case OmsSettableFields.INTERNAL_NOTE: {
      joinText = "to";
      row2Fields = (
        <>
          <RedoTextInput
            placeholder="Enter internal note"
            setValue={(value) => setStep({ ...step, note: value })}
            value={(step as { note?: string }).note || ""}
          />
          <Text fontSize="xs" textColor="secondary">
            This will not override existing internal notes if automations are
            run again. If no internal note exists, this will be set.
          </Text>
        </>
      );
      break;
    }
    case OmsSettableFields.SPLIT_INTO_PARCELS: {
      row2Fields = (
        <Text fontSize="xs" textColor="secondary">
          Items marked with "own package" will each be placed in their own
          parcel.
        </Text>
      );
      break;
    }
    // No default
  }

  return (
    <Flex dir="column" justify="flex-start">
      <Flex justify="space-between" py="sm" w="full">
        <Text fontSize="sm" fontWeight="semibold">
          Action
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>
      <Flex align="center" dir="row" gap="md" overflow="visible" wrap="wrap">
        <div style={{ maxWidth: "250px" }}>
          <RedoSingleSelectDropdownInput<OmsSettableFields>
            fitToAnchor={false}
            flexGrow={false}
            options={filteredOptions}
            optionSelected={setField}
            placeholder="Select an action"
            searchPlaceholder="Search actions..."
            searchString={searchString}
            selectedItem={omsOptions.find((o) => o.id === step.field)}
            setSearchString={setSearchString}
            size="xs"
          />
        </div>
        {joinText ? (
          <Text fontSize="xs" fontWeight="medium">
            {joinText}
          </Text>
        ) : null}
        {row1Fields}
      </Flex>
      {row2Fields}
    </Flex>
  );
});

export const omsActionStepType: FrontendStepType<
  PartialWithRequiredType<OmsActionStep>
> = {
  valid: (step) => omsActionStepSchema.safeParse(step).success,
  type: "custom",
  customInspector: OmsActionInputs,
};
